import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { useState } from "react";
import { colors, inr } from "@/theme";
import { StarRating } from "@/components/StarRating";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

export function ProductCard({ product, onWishlistChange, inWishlist = false }) {
  const { user } = useAuth();
  const [wished, setWished] = useState(inWishlist);
  const img = (product.images && product.images[0]) || "";
  const onSale = product.original_price && product.original_price > product.price;

  const toggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast("Sign in to save favourites");
      return;
    }
    try {
      const res = await api("/wishlist/toggle", { method: "POST", body: { product_id: product.id } });
      setWished(res.in_wishlist);
      onWishlistChange?.(product.id, res.in_wishlist);
      toast(res.in_wishlist ? "Added to wishlist" : "Removed from wishlist");
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Link
      to={`/product/${product.id}`}
      data-testid={`product-card-${product.id}`}
      className="card-hover font-body"
      style={{ textDecoration: "none", color: colors.onSurface, display: "block" }}
    >
      <div
        style={{
          position: "relative",
          aspectRatio: "3 / 4",
          overflow: "hidden",
          border: `1px solid ${colors.border}`,
          background: colors.surfaceSecondary,
        }}
      >
        {img ? (
          <img className="card-img" src={img} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} loading="lazy" />
        ) : null}
        {onSale && (
          <span
            style={{
              position: "absolute", top: 10, left: 10, background: colors.sale, color: "#3D1A19",
              fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", padding: "4px 9px", borderRadius: 999,
            }}
          >
            Sale
          </span>
        )}
        {product.stock_status === "out_of_stock" && (
          <span data-testid={`badge-oos-${product.id}`} style={{ position: "absolute", bottom: 10, left: 10, background: colors.onSurface, color: colors.surface, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", padding: "4px 9px", borderRadius: 999 }}>
            Out of Stock
          </span>
        )}
        {product.stock_status === "few_left" && (
          <span data-testid={`badge-fewleft-${product.id}`} style={{ position: "absolute", bottom: 10, left: 10, background: colors.accentDark, color: "#3D1A19", fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", padding: "4px 9px", borderRadius: 999 }}>
            Few Stocks Left
          </span>
        )}
        <button
          data-testid={`wishlist-toggle-${product.id}`}
          onClick={toggleWishlist}
          aria-label="Toggle wishlist"
          style={{
            position: "absolute", top: 8, right: 8, width: 34, height: 34, borderRadius: 999,
            background: "rgba(252,251,248,0.9)", border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(6px)",
          }}
        >
          <Heart size={16} fill={wished ? colors.accentDark : "none"} color={wished ? colors.accentDark : colors.onSurface} strokeWidth={1.6} />
        </button>
      </div>
      <div style={{ padding: "12px 2px 4px" }}>
        <h3 className="clamp-1" style={{ margin: 0, fontSize: 14, fontWeight: 500 }}>{product.name}</h3>
        <div style={{ marginTop: 5, display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>{inr(product.price)}</span>
          {onSale && <span style={{ fontSize: 12.5, color: colors.mutedText, textDecoration: "line-through" }}>{inr(product.original_price)}</span>}
        </div>
        {product.review_count > 0 && (
          <div style={{ marginTop: 6 }}>
            <StarRating rating={product.average_rating} count={product.review_count} size={12} />
          </div>
        )}
      </div>
    </Link>
  );
}
